<template>
  <div class="family-order-detail family-page bg-slate-50">
    <header
      class="family-page-header bg-white/90 backdrop-blur-md border-b border-slate-100"
      :style="{ paddingTop: 'env(safe-area-inset-top)' }"
    >
      <div class="px-4 py-3 flex items-center gap-3">
        <button
          type="button"
          class="w-9 h-9 rounded-full bg-slate-50 flex items-center justify-center text-slate-500"
          @click="goBack"
        >
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 class="text-base font-bold text-slate-900">订单详情</h1>
          <p v-if="order" class="text-xs text-slate-400">{{ order.orderNo }}</p>
        </div>
      </div>
    </header>

    <main v-if="loading" class="family-page-body family-page-body--center text-sm text-slate-400">
      加载中...
    </main>

    <main v-else-if="order" class="family-page-body px-4 py-4 pb-28">
      <section class="status-hero" :class="heroClass">
        <div class="status-hero-top">
          <div>
            <p class="status-hero-label">当前状态</p>
            <h2 class="status-hero-title">{{ order.statusLabel || statusStyle(order.status).label }}</h2>
          </div>
          <span v-if="assignBadge" class="status-hero-badge">{{ assignBadge }}</span>
        </div>
        <p v-if="order.dispatchHint" class="status-hero-hint">{{ order.dispatchHint }}</p>
        <div class="status-timeline">
          <div
            v-for="(step, idx) in timelineSteps"
            :key="step.key"
            class="timeline-item"
            :class="{ active: step.active, done: step.done }"
          >
            <div class="timeline-dot-wrap">
              <div class="timeline-dot" />
              <div v-if="idx < timelineSteps.length - 1" class="timeline-line" />
            </div>
            <span class="timeline-label">{{ step.label }}</span>
          </div>
        </div>
      </section>

      <section class="info-card">
        <div class="info-card-head">
          <span class="info-icon info-icon-blue">🏥</span>
          <h3>医院信息</h3>
        </div>
        <p class="info-main">{{ order.hospital }}</p>
        <p class="info-sub">{{ order.department }} · {{ order.bedNo }}</p>
      </section>

      <section class="info-card">
        <div class="info-card-head">
          <span class="info-icon info-icon-teal">📅</span>
          <h3>服务时间</h3>
        </div>
        <div class="service-time-row">
          <div>
            <p class="info-label">看护周期</p>
            <p class="info-main">{{ order.dateRange }}</p>
          </div>
          <div v-if="order.estimatedDays" class="days-pill">
            共 {{ order.estimatedDays }} 天
          </div>
        </div>
        <p v-if="order.createTime" class="info-sub mt-2">提交时间：{{ order.createTime }}</p>
      </section>

      <section class="info-card">
        <div class="info-card-head">
          <span class="info-icon info-icon-purple">🧑‍⚕️</span>
          <h3>患者情况</h3>
        </div>
        <div class="patient-grid">
          <div class="patient-cell">
            <p class="info-label">姓名</p>
            <p class="info-value">{{ order.patientName }}</p>
          </div>
          <div class="patient-cell">
            <p class="info-label">性别</p>
            <p class="info-value">{{ order.gender || "—" }}</p>
          </div>
          <div class="patient-cell patient-cell-full">
            <p class="info-label">自理能力</p>
            <p class="info-value">{{ order.careLevel || "—" }}</p>
          </div>
        </div>
      </section>

      <section class="info-card">
        <div class="info-card-head">
          <span class="info-icon info-icon-amber">👷</span>
          <h3>护工安排</h3>
        </div>

        <div v-if="order.caregiver" class="caregiver-block confirmed">
          <div class="caregiver-block-label">
            <span class="dot dot-green" />
            已确认护工
          </div>
          <div class="caregiver-row">
            <div
              class="caregiver-avatar"
              :class="order.caregiver.avatarBg"
            >
              {{ order.caregiver.name.charAt(0) }}
            </div>
            <div>
              <p class="caregiver-name">{{ order.caregiver.name }}</p>
              <p class="caregiver-level">{{ order.caregiver.level }}</p>
            </div>
          </div>
        </div>

        <div v-else-if="order.preferredCaregiver" class="caregiver-block pending">
          <div class="caregiver-block-label">
            <span class="dot dot-amber" />
            意向护工（待调度确认）
          </div>
          <div class="caregiver-row">
            <div
              class="caregiver-avatar"
              :class="order.preferredCaregiver.avatarBg"
            >
              {{ order.preferredCaregiver.name.charAt(0) }}
            </div>
            <div>
              <p class="caregiver-name">{{ order.preferredCaregiver.name }}</p>
              <p class="caregiver-level">{{ order.preferredCaregiver.level }}</p>
            </div>
          </div>
          <p class="pending-note">最终以管理端指派结果为准，调度员可能根据排班安排其他护工</p>
        </div>

        <div v-else class="caregiver-block random">
          <div class="caregiver-block-label">
            <span class="dot dot-blue" />
            随机分配
          </div>
          <div class="random-box">
            <span class="random-icon">🎲</span>
            <div>
              <p class="caregiver-name">等待调度员安排</p>
              <p class="caregiver-level">将根据医院与排班为您匹配合适护工</p>
            </div>
          </div>
        </div>
      </section>

      <section v-if="order.canReview" class="review-invite-card">
        <div class="review-invite-icon">⭐</div>
        <div class="review-invite-body">
          <h3>服务已结束，欢迎评价</h3>
          <p>您的反馈将帮助其他家属选择护工，也能激励护工提供更好的服务。</p>
        </div>
      </section>

      <section v-if="order.review" class="info-card">
        <div class="info-card-head">
          <span class="info-icon info-icon-green">⭐</span>
          <h3>我的评价</h3>
        </div>
        <div class="review-scores">
          <span class="review-score-pill">综合 {{ order.review.starRating }}</span>
          <span v-if="order.review.professionalismRating" class="review-score-pill">专业 {{ order.review.professionalismRating }}</span>
          <span v-if="order.review.attitudeRating" class="review-score-pill">态度 {{ order.review.attitudeRating }}</span>
        </div>
        <p class="review-text">{{ order.review.content || "用户未填写文字评价" }}</p>
      </section>
    </main>

    <div
      v-if="order && order.canReview"
      class="fixed left-0 right-0 z-30 px-4 pb-4 bg-gradient-to-t from-slate-50 via-slate-50/95 to-transparent pt-3"
      :style="{ bottom: 'env(safe-area-inset-bottom)' }"
    >
      <button
        type="button"
        class="w-full py-3.5 rounded-xl text-white text-sm font-semibold bg-family-blue shadow-[0_6px_20px_rgb(0,130,212,0.35)]"
        @click="goReview"
      >
        去评价护工
      </button>
    </div>
  </div>
</template>

<script>
import { familyOrderApi } from "./familyOrderApi";
import { familyGoBack } from "./familyNavigation";

const STATUS_MAP = {
  pending: { label: "待派单", tag: "bg-red-50 text-red-500", hero: "hero-pending" },
  serving: { label: "服务中", tag: "bg-emerald-50 text-emerald-600", hero: "hero-serving" },
  pending_confirm: { label: "待确认完工", tag: "bg-orange-50 text-orange-500", hero: "hero-confirm" },
  done: { label: "已完工", tag: "bg-slate-100 text-slate-500", hero: "hero-done" },
};

const TIMELINE = [
  { key: "submit", label: "提交需求" },
  { key: "dispatch", label: "调度确认" },
  { key: "accept", label: "护工接单" },
  { key: "serve", label: "服务中" },
  { key: "done", label: "完工" },
];

export default {
  name: "FamilyOrderDetail",
  data() {
    return {
      loading: true,
      order: null,
    };
  },
  computed: {
    heroClass() {
      const s = this.order ? Number(this.order.orderStatus) : 0;
      if (s === 0) return "hero-pending";
      if (s === 1 || s === 2) return "hero-dispatch";
      if (s >= 3 && s <= 5) return "hero-serving";
      if (s === 6) return "hero-confirm";
      return "hero-done";
    },
    assignBadge() {
      const o = this.order;
      if (!o) return "";
      if (o.assignModeLabel) return o.assignModeLabel;
      if (o.caregiver) return "已指派护工";
      if (o.preferredCaregiver) return "意向护工";
      if (o.assignMode === "random") return "随机分配";
      return "";
    },
    timelineSteps() {
      const status = this.order ? Number(this.order.orderStatus) : 0;
      if (status >= 7) {
        return TIMELINE.map((step) => ({ ...step, done: true, active: false }));
      }
      const activeIdx = this.resolveTimelineIndex(status);
      return TIMELINE.map((step, idx) => ({
        ...step,
        done: idx < activeIdx,
        active: idx === activeIdx,
      }));
    },
  },
  mounted() {
    document.title = "订单详情 - 安心陪护";
    document.documentElement.classList.add("family-route");
    document.body.classList.add("family-route");
    this.loadDetail();
  },
  beforeDestroy() {
    document.documentElement.classList.remove("family-route");
    document.body.classList.remove("family-route");
  },
  methods: {
    resolveTimelineIndex(status) {
      const s = status == null ? 0 : Number(status);
      if (s === 0) return 1;
      if (s === 1) return 2;
      if (s === 2) return 2;
      if (s >= 3 && s <= 5) return 3;
      if (s === 6) return 4;
      if (s >= 7) return 5;
      return 1;
    },
    statusStyle(status) {
      return STATUS_MAP[status] || STATUS_MAP.pending;
    },
    async loadDetail() {
      this.loading = true;
      try {
        this.order = await familyOrderApi.detail(this.$route.params.id);
      } catch (e) {
        this.$message.error((e && e.message) || "加载订单失败");
        this.goBack();
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      familyGoBack(this.$router, this.$route, "orders");
    },
    goReview() {
      this.$router.push({
        path: `/family/order/${this.$route.params.id}/review`,
        query: this.$route.query,
      });
    },
  },
};
</script>

<style scoped>
.family-order-detail {
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Hiragino Sans GB",
    "Microsoft YaHei", sans-serif;
}
.status-hero {
  border-radius: 1rem;
  padding: 1rem;
  margin-bottom: 0.75rem;
  color: #fff;
  box-shadow: 0 8px 24px rgb(0 0 0 / 0.08);
}
.hero-pending {
  background: linear-gradient(135deg, #f97316, #ef4444);
}
.hero-dispatch {
  background: linear-gradient(135deg, #38bdf8, #0284c7);
}
.hero-serving {
  background: linear-gradient(135deg, #0ea5e9, #0369a1);
}
.hero-confirm {
  background: linear-gradient(135deg, #f59e0b, #ea580c);
}
.hero-done {
  background: linear-gradient(135deg, #64748b, #475569);
}
.status-hero-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}
.status-hero-label {
  font-size: 11px;
  color: rgb(255 255 255 / 0.88);
}
.status-hero-title {
  font-size: 1.25rem;
  font-weight: 700;
  margin-top: 0.125rem;
  color: #fff;
}
.status-hero-badge {
  flex-shrink: 0;
  font-size: 11px;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  background: rgb(255 255 255 / 0.2);
  backdrop-filter: blur(4px);
}
.status-hero-hint {
  margin-top: 0.625rem;
  font-size: 12px;
  line-height: 1.5;
  opacity: 0.95;
}
.status-timeline {
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgb(255 255 255 / 0.2);
}
.timeline-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
}
.timeline-dot-wrap {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 1rem;
}
.timeline-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  background: rgb(255 255 255 / 0.35);
  z-index: 1;
}
.timeline-item.done .timeline-dot,
.timeline-item.active .timeline-dot {
  background: #fff;
  box-shadow: 0 0 0 3px rgb(255 255 255 / 0.25);
}
.timeline-line {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 100%;
  height: 2px;
  background: rgb(255 255 255 / 0.25);
  transform: translateY(-50%);
}
.timeline-label {
  margin-top: 0.375rem;
  font-size: 10px;
  text-align: center;
  opacity: 0.8;
  white-space: nowrap;
}
.timeline-item.active .timeline-label {
  opacity: 1;
  font-weight: 600;
}
.info-card {
  background: #fff;
  border-radius: 1rem;
  padding: 1rem;
  margin-bottom: 0.75rem;
  box-shadow: 0 2px 12px rgb(0 0 0 / 0.04);
}
.info-card-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
.info-card-head h3 {
  font-size: 0.875rem;
  font-weight: 700;
  color: #1e293b;
}
.info-icon {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
}
.info-icon-blue { background: #eff6ff; }
.info-icon-teal { background: #f0fdfa; }
.info-icon-purple { background: #faf5ff; }
.info-icon-amber { background: #fffbeb; }
.info-icon-green { background: #ecfdf5; }
.info-main {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #0f172a;
}
.info-sub {
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 0.25rem;
}
.info-label {
  font-size: 11px;
  color: #94a3b8;
}
.info-value {
  font-size: 0.875rem;
  color: #1e293b;
  font-weight: 500;
  margin-top: 0.125rem;
}
.service-time-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}
.days-pill {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  color: #0369a1;
  background: #e0f2fe;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
}
.patient-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}
.patient-cell-full {
  grid-column: span 2;
}
.caregiver-block {
  border-radius: 0.75rem;
  padding: 0.75rem;
}
.caregiver-block.confirmed {
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
}
.caregiver-block.pending {
  background: #fffbeb;
  border: 1px solid #fde68a;
}
.caregiver-block.random {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
}
.caregiver-block-label {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 0.625rem;
}
.dot {
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 999px;
}
.dot-green { background: #10b981; }
.dot-amber { background: #f59e0b; }
.dot-blue { background: #0ea5e9; }
.caregiver-row,
.random-box {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.caregiver-avatar {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
  flex-shrink: 0;
}
.caregiver-name {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #0f172a;
}
.caregiver-level {
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 0.125rem;
}
.pending-note {
  margin-top: 0.625rem;
  font-size: 11px;
  color: #b45309;
  line-height: 1.5;
}
.random-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}
.review-text {
  font-size: 0.875rem;
  color: #475569;
  line-height: 1.6;
}
.review-scores {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-bottom: 0.5rem;
}
.review-score-pill {
  font-size: 11px;
  color: #b45309;
  background: #fffbeb;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
}
.review-invite-card {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  background: linear-gradient(135deg, #fffbeb, #fef3c7);
  border: 1px solid #fde68a;
  border-radius: 1rem;
  padding: 1rem;
  margin-bottom: 0.75rem;
}
.review-invite-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}
.review-invite-body h3 {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #92400e;
}
.review-invite-body p {
  margin: 0.375rem 0 0;
  font-size: 0.75rem;
  color: #b45309;
  line-height: 1.5;
}
</style>
